import { create, StateCreator } from 'zustand';
import { AxiosError, isAxiosError } from 'axios';
import dayjs from 'dayjs';
import {
  StaffHasActiveShiftsErrorDetails,
  StaffListItem,
  StaffCreateData,
  StaffDetails,
  StaffEditData,
  StaffHasActiveShiftsError,
} from '@libs/models/talents';
import { ErrorWithDetails } from '@libs/utils';
import {
  activateTalents,
  addTalentCredential,
  createTalent,
  deactivateTalents,
  editTalent,
  getTalent,
  getTalents,
  removeTalentCredential,
  type GetTalentsParams,
} from '@libs/api/talents';
import { parseError } from '@libs/api/errors';
import { CredentialsType } from '@libs/models/talents/talent';

type TalentsListState = {
  loading: boolean;
  initiated: boolean;
  total: number;
  load: (params?: GetTalentsParams) => Promise<void>;
  talents: StaffListItem[];
  selectedTalents: string[];
  setSelectedTalents: (talents: string[]) => void;
  deactivateTalents: (ids: string[]) => ReturnType<typeof deactivateTalents>;
  activateTalents: (ids: string[]) => Promise<void>;
};

const createTalentsListSlice: StateCreator<TalentsListState, [], [], TalentsListState> = (set, get) => ({
  loading: false,
  initiated: false,
  load: async (params) => {
    set({ loading: true });
    try {
      const data = await getTalents({
        ...params,
      });
      set({
        talents: data.items.map((t) => ({
          ...t,
          lastWorkedTime: dayjs(t.lastWorkedTime),
        })),
        total: data.totalCount,
        initiated: true,
        loading: false,
      });
    } finally {
      set({ loading: false });
    }
  },
  total: 0,
  talents: [],
  selectedTalents: [],
  setSelectedTalents: (talents) => {
    set({ selectedTalents: talents });
  },
  deactivateTalents: async (ids: string[]) => {
    try {
      const result = await deactivateTalents(ids);
      return result;
    } catch (error) {
      if (isAxiosError(error)) {
        if (isStaffHasActiveShiftsError(error) && error.response) {
          throw new StaffHasActiveShiftsError(error.response.data);
        }
        if (typeof error.response?.data === 'object') {
          throw new ErrorWithDetails(error.response?.data.detail, { type: error.response?.data.status, values: {} });
        }
      }
      throw error;
    }
  },
  activateTalents: async (ids: string[]) => {
    await activateTalents(ids);
  },
});

type TalentsEditState = {
  open: boolean;
  saving: boolean;
  showEditForm: boolean;
  showCreateForm: boolean;
  editInitial: StaffDetails | null;
  editLoading: boolean;
  createTalent: (data: StaffCreateData) => Promise<void>;
  saveTalent: (id: string, data: StaffEditData) => Promise<void>;
  openCreateForm: () => void;
  openEditForm: (id: string) => Promise<void>;
  closeEditForm: () => void;
  addCredential: (fileFile: File, type: CredentialsType, fileName: string) => Promise<void>;
  removeCredential: (filePath: string) => Promise<void>;
  reloadCurrentTalentData: (silent?: boolean) => Promise<void>;
};
const createTalentsEditSlice: StateCreator<TalentsListState & TalentsEditState, [], [], TalentsEditState> = (
  set,
  get,
) => ({
  open: false,
  saving: false,
  editInitial: null,
  editLoading: false,
  showCreateForm: false,
  showEditForm: false,
  createTalent: async (data) => {
    set({ saving: true });
    try {
      await createTalent(data);
    } catch (error) {
      throw parseError(error, 'talents.editForm.errors');
    } finally {
      set({ saving: false });
    }
  },
  saveTalent: async (id, data) => {
    set({ saving: true });
    try {
      await editTalent(id, data);
    } catch (error) {
      if (isAxiosError(error)) {
        throw error.response?.data;
      }
      throw error;
    } finally {
      set({ saving: false });
    }
  },

  openCreateForm: () => {
    set({ showCreateForm: true, open: true });
  },
  closeEditForm: () => {
    set({ showEditForm: false, showCreateForm: false, editLoading: false, editInitial: null, open: false });
  },
  openEditForm: async (id) => {
    set({ showEditForm: true, open: true, editLoading: true });
    const talent = await getTalent(id);
    set({
      editLoading: false,
      editInitial: talent,
    });
  },
  addCredential: async (fileFile: File, type: CredentialsType, fileName: string) => {
    const { editInitial, editLoading } = get();
    if (!editInitial || editLoading) {
      throw new Error('Cannot add credential: no talent loaded');
    }
    await addTalentCredential(editInitial.id, fileFile, type, fileName);
  },
  removeCredential: async (filePath: string) => {
    const { editInitial, editLoading } = get();
    if (!editInitial || editLoading) {
      throw new Error('Cannot remove credential: no talent loaded');
    }
    await removeTalentCredential(editInitial.id, filePath);
  },
  reloadCurrentTalentData: async (silent: boolean = false) => {
    const { editInitial, editLoading } = get();
    if (!editInitial || editLoading) {
      throw new Error('Cannot reload talent: no talent loaded');
    }
    !silent && set({ editLoading: true });
    const talent = await getTalent(editInitial.id);
    set({
      editLoading: false,
      editInitial: talent,
    });
  },
});

export const useTalentsStore = create<TalentsListState & TalentsEditState>()((...a) => ({
  ...createTalentsListSlice(...a),
  ...createTalentsEditSlice(...a),
}));

function isStaffHasActiveShiftsError(error: unknown): error is AxiosError<StaffHasActiveShiftsErrorDetails> {
  return (
    isAxiosError(error) && (error.response?.data as StaffHasActiveShiftsErrorDetails)?.type === 'staffHasActiveShifts'
  );
}
